import React from "react";
import styled, { keyframes } from "styled-components";

const shake = keyframes`
  0%,100%{transform:translateX(0)}
  20%{transform:translateX(-5px)}
  40%{transform:translateX(5px)}
  60%{transform:translateX(-3px)}
  80%{transform:translateX(3px)}
`;
const fadeIn = keyframes`from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}`;

/* ─── Global error banner ────────────────────────────────────────────────── */
export const ErrorBanner = styled.div`
  display: ${({$show}) => $show ? "flex" : "none"};
  align-items: flex-start;
  gap: .5rem;
  background: ${({theme}) => theme.redDim};
  border: 1px solid ${({theme}) => theme.red}55;
  border-radius: 8px;
  padding: .7rem .9rem;
  font-size: .78rem;
  color: ${({theme}) => theme.red};
  line-height: 1.5;
  animation: ${fadeIn} .2s ease, ${shake} .4s ease;
`;

/* ─── Field-level error ───────────────────────────────────────────────────── */
export const FieldError = styled.span`
  display: ${({$show}) => $show ? "flex" : "none"};
  align-items: center;
  gap: .3rem;
  font-size: .68rem;
  color: ${({theme}) => theme.red};
  animation: ${fadeIn} .15s ease;
  &::before { content: "⚠"; font-size: .65rem; }
`;

/* ─── Success banner ─────────────────────────────────────────────────────── */
export const SuccessBanner = styled.div`
  display: ${({$show}) => $show ? "flex" : "none"};
  align-items: center;
  gap: .5rem;
  background: ${({theme}) => theme.greenDim};
  border: 1px solid ${({theme}) => theme.green}55;
  border-radius: 8px;
  padding: .65rem .9rem;
  font-size: .78rem;
  color: ${({theme}) => theme.green};
  animation: ${fadeIn} .2s ease;
`;

/* ─── Utility: parse Django REST errors into a flat list ─────────────────── */
export function parseApiErrors(err) {
  if (!err?.response?.data) return null;
  const data = err.response.data;

  // { "detail": "..." }
  if (typeof data === "string") return data;
  if (data.detail)               return data.detail;

  // { "field": ["error1", "error2"], ... }
  if (typeof data === "object") {
    const messages = [];
    Object.entries(data).forEach(([field, errs]) => {
      const list = Array.isArray(errs) ? errs : [errs];
      list.forEach(msg => {
        const prefix = field !== "non_field_errors" ? `${field} : ` : "";
        messages.push(`${prefix}${msg}`);
      });
    });
    if (messages.length) return messages.join("\n");
  }

  return "Une erreur inattendue s'est produite. Veuillez réessayer.";
}

/* ─── Hook: field-level error state ─────────────────────────────────────── */
export function useFormErrors() {
  const [errors, setErrors] = React.useState({});
  const [globalError, setGlobalError] = React.useState(null);

  const setFieldError = (field, message) =>
    setErrors(prev => ({ ...prev, [field]: message }));

  const clearFieldError = (field) =>
    setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const setFromApi = (err) => {
    if (!err?.response?.data) {
      setGlobalError("Une erreur inattendue s'est produite.");
      return;
    }
    const data = err.response.data;

    if (typeof data === "string") { setGlobalError(data); return; }
    if (data.detail)               { setGlobalError(data.detail); return; }

    if (typeof data === "object") {
      const fieldErrs = {};
      let hasNonField = false;
      Object.entries(data).forEach(([field, errs]) => {
        const msg = Array.isArray(errs) ? errs.join(" ") : String(errs);
        if (field === "non_field_errors") {
          setGlobalError(msg);
          hasNonField = true;
        } else {
          fieldErrs[field] = msg;
        }
      });
      setErrors(fieldErrs);
      if (!hasNonField) setGlobalError(null);
    }
  };

  const clearAll = () => { setErrors({}); setGlobalError(null); };

  return {
    errors,
    globalError,
    setFieldError,
    clearFieldError,
    setGlobalError,
    setFromApi,
    clearAll,
    hasErrors: Object.keys(errors).length > 0 || !!globalError,
  };
}
